import { SlateElement, SlateNode } from '@all-hands-hq/plex-graphql-schema';
import { Editor, Node, Path, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

/* eslint-disable @typescript-eslint/ban-ts-comment */
export function withVoidElements<T extends ReactEditor>(editor: T): T {
  const { isVoid, normalizeNode, deleteBackward, insertBreak } = editor;

  // @ts-ignore
  editor.isVoid = (element: SlateElement): boolean => {
    if (element.type === 'image' || element.type === 'upload_in_progress' || element.type === 'expanded_link') {
      return true;
    } else {
      // @ts-ignore
      return isVoid(element);
    }
  };

  /* This is triggered for each changed node and each of its ancestors after every Slate editor operation */
  editor.normalizeNode = ([currentNode, currentPath]): void => {
    const FIRST_ROW_PATH: Path = [0];

    // Ensure first row is never a void element
    if (Editor.isVoid(editor, currentNode) && Path.equals(currentPath, FIRST_ROW_PATH)) {
      Transforms.insertNodes(editor, [{ type: 'paragraph', children: [{ text: '' }] }], {
        at: FIRST_ROW_PATH,
      });
    }

    normalizeNode([currentNode, currentPath]);
  };

  /**
   * Insert a paragraph bellow if we are currently on a `plex_link` element
   */
  editor.insertBreak = (): void => {
    const hasVoidElementSelected =
      editor.selection != null &&
      Range.isCollapsed(editor.selection) &&
      Editor.above(editor, { match: (n) => Editor.isVoid(editor, n) }) != null;

    if (hasVoidElementSelected) {
      const node: SlateNode = { type: 'paragraph', children: [{ text: '' }] };
      // @ts-ignore
      editor.insertNode(node as Node);
      return;
    }

    insertBreak();
  };

  /* This is triggered when deleting backward (backspace) while the selection is collapsed (a single caret) */
  editor.deleteBackward = (unit: 'character' | 'word' | 'line' | 'block'): void => {
    if (editor.selection != null) {
      const { anchor } = editor.selection;
      if (anchor.offset === 0) {
        const hasVoidElementOnPreviousUnit =
          Editor.above(editor, {
            match: (n) => Editor.isVoid(editor, n),
            at: Editor.before(editor, anchor, { distance: 1, unit }),
          }) != null;

        if (hasVoidElementOnPreviousUnit) {
          const blockEntry = Editor.above(editor, {
            match: (n) => Editor.isBlock(editor, n),
            at: anchor,
          });

          if (blockEntry != null) {
            const [block, blockPath] = blockEntry;
            if (Editor.isVoid(editor, block)) {
              // Nothing to do, this element is selected and default behavior makes sense
            } else if (Editor.isEmpty(editor, block)) {
              // We want to delete the current line and move selection bellow
              Transforms.delete(editor, { at: blockPath });
              return;
            } else {
              // Do not perform delete if we're bellow a plex_link and we haven't selected it.
              // Instead select it by moving the anchor selection to the previous line
              Transforms.move(editor, { unit: 'line', distance: 1, reverse: true, edge: 'anchor' });
              return;
            }
          }
        }
      }
    }

    deleteBackward(unit);
  };

  /* This is triggered when deleting while the selection is expanded (selection is highlighted from `anchor` to `focus`) */
  editor.deleteFragment = (): void => {
    const { selection } = editor;

    if (selection && Range.isExpanded(selection)) {
      /*
      Important to pass `hanging: true` otherwise the un-hanging process will expand the selection to include all neighboring void nodes.
      For an explanation on hanging, see https://github.com/ianstormtaylor/slate/issues/3683#issuecomment-634581973
       */
      Transforms.delete(editor, { hanging: true });
    }
  };

  return editor;
}
